/**
 * Field Glossary - Definitions for fields, metrics, and terms used throughout Uni-Fund
 *
 * This glossary is used by the AI Help Assistant to explain fields and metrics
 * when users ask "What is [field]?" or "Explain [metric]".
 *
 * Structure:
 * - key: Field identifier (lowercase, hyphenated)
 * - label: Display name
 * - description: Plain English explanation
 * - calculation: How the value is calculated (if applicable)
 * - context: Where this field appears (pages/features)
 * - relatedFields: Related fields for additional context
 */

export const FIELD_GLOSSARY = [
  // ============================================
  // BUDGET & FINANCIAL METRICS
  // ============================================
  {
    key: 'original-budget',
    label: 'Original Budget',
    description: 'The initial approved budget allocation for a cost center or account at the start of the fiscal year.',
    calculation: 'Set during annual budget planning and approval process.',
    context: ['Budget Overview', 'Position Budgeting', 'Reports'],
    relatedFields: ['adjusted-budget', 'current-budget', 'budget-variance'],
  },
  {
    key: 'adjusted-budget',
    label: 'Adjusted Budget',
    description: 'The budget after all approved transfers, amendments, and adjustments have been applied.',
    calculation: 'Original Budget + Budget Transfers + Amendments',
    context: ['Budget Overview', 'Budget Transfers', 'Reports'],
    relatedFields: ['original-budget', 'current-budget', 'budget-transfers'],
  },
  {
    key: 'current-budget',
    label: 'Current Budget',
    description: 'The most up-to-date budget figure including all adjustments. This is what you have available to spend.',
    calculation: 'Same as Adjusted Budget in most cases.',
    context: ['Budget Overview', 'Dashboard', 'Reports'],
    relatedFields: ['adjusted-budget', 'available-balance', 'ytd-actuals'],
  },
  {
    key: 'ytd-actuals',
    label: 'YTD Actuals',
    description: 'Year-to-Date Actuals - The total amount actually spent or received from the start of the fiscal year until now.',
    calculation: 'Sum of all posted transactions from fiscal year start to current date.',
    context: ['Budget Overview', 'Reports', 'Dashboard'],
    relatedFields: ['encumbrances', 'available-balance', 'budget-variance'],
  },
  {
    key: 'encumbrances',
    label: 'Encumbrances',
    description: 'Funds that are committed but not yet spent. This includes purchase orders, contracts, and salary commitments.',
    calculation: 'Sum of all open POs + committed salary expenses + other reservations.',
    context: ['Budget Overview', 'Position Budgeting', 'Reports'],
    relatedFields: ['ytd-actuals', 'available-balance', 'pre-encumbrances'],
  },
  {
    key: 'pre-encumbrances',
    label: 'Pre-Encumbrances',
    description: 'Funds reserved for planned purchases that haven\'t been formally committed yet, like requisitions pending approval.',
    calculation: 'Sum of pending requisitions and planned expenses.',
    context: ['Budget Overview', 'Requisitions'],
    relatedFields: ['encumbrances', 'available-balance'],
  },
  {
    key: 'available-balance',
    label: 'Available Balance',
    description: 'The remaining budget you can spend. This accounts for what\'s already spent and what\'s committed.',
    calculation: 'Current Budget - YTD Actuals - Encumbrances - Pre-Encumbrances',
    context: ['Budget Overview', 'Dashboard', 'Reports'],
    relatedFields: ['current-budget', 'ytd-actuals', 'encumbrances'],
  },
  {
    key: 'budget-variance',
    label: 'Budget Variance',
    description: 'The difference between budgeted and actual amounts. Positive means under budget, negative means over budget.',
    calculation: 'Current Budget - YTD Actuals (or sometimes includes encumbrances)',
    context: ['Reports', 'Dashboard', 'Budget Overview'],
    relatedFields: ['current-budget', 'ytd-actuals', 'variance-percentage'],
  },
  {
    key: 'variance-percentage',
    label: 'Variance %',
    description: 'Budget variance expressed as a percentage of the total budget.',
    calculation: '(Budget Variance / Current Budget) x 100',
    context: ['Reports', 'Dashboard'],
    relatedFields: ['budget-variance', 'current-budget'],
  },
  {
    key: 'burn-rate',
    label: 'Burn Rate',
    description: 'The rate at which funds are being spent over time. Helps predict if you\'ll stay within budget.',
    calculation: 'YTD Actuals / Number of months elapsed in fiscal year.',
    context: ['Dashboard', 'Reports', 'Budget Projections'],
    relatedFields: ['ytd-actuals', 'projected-year-end'],
  },
  {
    key: 'projected-year-end',
    label: 'Projected Year-End',
    description: 'Estimated total spending by the end of the fiscal year based on current spending patterns.',
    calculation: 'Burn Rate x 12 months (or remaining months + YTD).',
    context: ['Reports', 'Budget Projections', 'Dashboard'],
    relatedFields: ['burn-rate', 'budget-variance'],
  },

  // ============================================
  // POSITION & SALARY FIELDS
  // ============================================
  {
    key: 'fte',
    label: 'FTE (Full-Time Equivalent)',
    description: 'A measure of employee workload. 1.0 FTE = full-time, 0.5 FTE = half-time.',
    calculation: 'Hours worked per week / Standard full-time hours (typically 40).',
    context: ['Position Budgeting', 'Position Management', 'Reports'],
    relatedFields: ['headcount', 'position-budget'],
  },
  {
    key: 'headcount',
    label: 'Headcount',
    description: 'The total number of employees, regardless of FTE. Each person counts as 1.',
    calculation: 'Count of unique employees in the department/cost center.',
    context: ['Position Management', 'Reports', 'Dashboard'],
    relatedFields: ['fte', 'vacant-positions'],
  },
  {
    key: 'position-budget',
    label: 'Position Budget',
    description: 'The total budgeted cost for a position including salary, benefits, and other compensation.',
    calculation: 'Base Salary + Benefits + Other Compensation (prorated by FTE if applicable).',
    context: ['Position Budgeting', 'Budget Overview'],
    relatedFields: ['fte', 'salary-savings', 'benefit-rate'],
  },
  {
    key: 'salary-savings',
    label: 'Salary Savings',
    description: 'Unspent salary funds from vacant positions or employees paid below budget.',
    calculation: 'Budgeted Position Cost - Actual Position Cost.',
    context: ['Position Budgeting', 'Budget Overview', 'Reports'],
    relatedFields: ['position-budget', 'vacant-positions'],
  },
  {
    key: 'vacant-positions',
    label: 'Vacant Positions',
    description: 'Positions that are budgeted but currently unfilled.',
    context: ['Position Management', 'Position Budgeting', 'Dashboard'],
    relatedFields: ['headcount', 'salary-savings'],
  },
  {
    key: 'benefit-rate',
    label: 'Benefit Rate',
    description: 'The percentage added to salary for benefits (health insurance, retirement, etc.).',
    calculation: 'Total Benefits Cost / Base Salary (typically 25-40%).',
    context: ['Position Budgeting', 'Position Management'],
    relatedFields: ['position-budget', 'fringe-rate'],
  },
  {
    key: 'fringe-rate',
    label: 'Fringe Rate',
    description: 'Similar to benefit rate - the employer\'s cost for benefits as a percentage of salary.',
    calculation: 'Employer-paid benefits / Gross salary.',
    context: ['Position Budgeting', 'Grants', 'Reports'],
    relatedFields: ['benefit-rate', 'position-budget'],
  },

  // ============================================
  // TRANSFER & TRANSACTION FIELDS
  // ============================================
  {
    key: 'transfer-from',
    label: 'Transfer From (Source)',
    description: 'The account or cost center that funds are being moved out of.',
    context: ['Budget Transfers', 'Journal Entries'],
    relatedFields: ['transfer-to', 'transfer-amount'],
  },
  {
    key: 'transfer-to',
    label: 'Transfer To (Destination)',
    description: 'The account or cost center that funds are being moved into.',
    context: ['Budget Transfers', 'Journal Entries'],
    relatedFields: ['transfer-from', 'transfer-amount'],
  },
  {
    key: 'transfer-amount',
    label: 'Transfer Amount',
    description: 'The dollar amount being moved between accounts or cost centers.',
    context: ['Budget Transfers', 'Journal Entries'],
    relatedFields: ['transfer-from', 'transfer-to'],
  },
  {
    key: 'posting-date',
    label: 'Posting Date',
    description: 'The date when a transaction is officially recorded in the financial system.',
    context: ['Transactions', 'Journal Entries', 'Reports'],
    relatedFields: ['transaction-date', 'fiscal-period'],
  },
  {
    key: 'transaction-date',
    label: 'Transaction Date',
    description: 'The date when a transaction actually occurred (may differ from posting date).',
    context: ['Transactions', 'Reports'],
    relatedFields: ['posting-date', 'fiscal-period'],
  },
  {
    key: 'fiscal-period',
    label: 'Fiscal Period',
    description: 'The accounting period (usually a month) for reporting purposes.',
    context: ['Reports', 'Transactions', 'Budget Overview'],
    relatedFields: ['fiscal-year', 'posting-date'],
  },
  {
    key: 'fiscal-year',
    label: 'Fiscal Year',
    description: 'The 12-month period used for annual budgeting and reporting. May not align with calendar year.',
    context: ['Reports', 'Budget Overview', 'Settings'],
    relatedFields: ['fiscal-period'],
  },

  // ============================================
  // FUND & ACCOUNT STRUCTURE
  // ============================================
  {
    key: 'fund',
    label: 'Fund',
    description: 'A self-balancing set of accounts used to track specific types of resources (e.g., Operating, Grants, Endowment).',
    context: ['Chart of Accounts', 'Budget Overview', 'Reports'],
    relatedFields: ['cost-center', 'account-code', 'program'],
  },
  {
    key: 'cost-center',
    label: 'Cost Center',
    description: 'An organizational unit (department, unit, project) that incurs costs. Used for budget tracking and reporting.',
    context: ['Budget Overview', 'Reports', 'Transactions'],
    relatedFields: ['fund', 'account-code', 'department'],
  },
  {
    key: 'account-code',
    label: 'Account Code',
    description: 'A numeric code that categorizes the type of expense or revenue (e.g., 5001 = Salaries, 6001 = Supplies).',
    context: ['Chart of Accounts', 'Transactions', 'Reports'],
    relatedFields: ['fund', 'cost-center', 'natural-account'],
  },
  {
    key: 'natural-account',
    label: 'Natural Account',
    description: 'The basic classification of what type of transaction it is (e.g., salary, travel, equipment).',
    context: ['Chart of Accounts', 'Reports'],
    relatedFields: ['account-code', 'expense-category'],
  },
  {
    key: 'program',
    label: 'Program',
    description: 'A grouping used to track spending for specific activities or initiatives across departments.',
    context: ['Chart of Accounts', 'Reports', 'Budget Overview'],
    relatedFields: ['fund', 'cost-center', 'project'],
  },
  {
    key: 'project',
    label: 'Project',
    description: 'A specific initiative or grant with its own budget tracking, often with start and end dates.',
    context: ['Project Management', 'Grants', 'Reports'],
    relatedFields: ['program', 'grant', 'cost-center'],
  },

  // ============================================
  // APPROVAL & WORKFLOW
  // ============================================
  {
    key: 'approval-status',
    label: 'Approval Status',
    description: 'The current state of a request in the approval workflow (Pending, Approved, Rejected, etc.).',
    context: ['Approvals', 'Budget Transfers', 'Requisitions'],
    relatedFields: ['approver', 'workflow-step'],
  },
  {
    key: 'approver',
    label: 'Approver',
    description: 'The person responsible for reviewing and approving a request.',
    context: ['Approvals', 'Workflow', 'Budget Transfers'],
    relatedFields: ['approval-status', 'workflow-step'],
  },
  {
    key: 'workflow-step',
    label: 'Workflow Step',
    description: 'The current stage in a multi-step approval process.',
    context: ['Approvals', 'Workflow Settings'],
    relatedFields: ['approval-status', 'approver'],
  },

  // ============================================
  // GRANTS & SPONSORED PROJECTS
  // ============================================
  {
    key: 'grant',
    label: 'Grant',
    description: 'External funding awarded for a specific purpose, often with spending restrictions and reporting requirements.',
    context: ['Grants', 'Research', 'Reports'],
    relatedFields: ['project', 'sponsor', 'indirect-costs'],
  },
  {
    key: 'indirect-costs',
    label: 'Indirect Costs (F&A)',
    description: 'Facilities & Administrative costs - overhead expenses charged to grants for institutional support.',
    calculation: 'Direct Costs x Indirect Cost Rate (negotiated with federal government).',
    context: ['Grants', 'Research', 'Reports'],
    relatedFields: ['direct-costs', 'grant'],
  },
  {
    key: 'direct-costs',
    label: 'Direct Costs',
    description: 'Expenses directly tied to a specific project or grant (salaries, supplies, equipment).',
    context: ['Grants', 'Research', 'Project Management'],
    relatedFields: ['indirect-costs', 'grant'],
  },
  {
    key: 'award-amount',
    label: 'Award Amount',
    description: 'The total funding amount granted by a sponsor for a project.',
    context: ['Grants', 'Research'],
    relatedFields: ['grant', 'project-budget'],
  },
  {
    key: 'cost-share',
    label: 'Cost Share',
    description: 'The portion of project costs that the institution agrees to pay, not covered by the grant.',
    context: ['Grants', 'Research'],
    relatedFields: ['grant', 'matching-funds'],
  },
];

/**
 * Page context definitions - what pages are about and what fields are typically shown
 */
export const PAGE_CONTEXT = {
  '/dashboard': {
    name: 'Dashboard',
    description: 'Your personalized overview of pending tasks, alerts, and key financial metrics.',
    commonFields: ['available-balance', 'budget-variance', 'ytd-actuals', 'pending-approvals'],
  },
  '/budget': {
    name: 'Budget Overview',
    description: 'View and manage budget allocations, spending, and balances across cost centers.',
    commonFields: ['original-budget', 'adjusted-budget', 'ytd-actuals', 'encumbrances', 'available-balance'],
  },
  '/budget/transfers': {
    name: 'Budget Transfers',
    description: 'Request, track, and approve budget transfers between accounts or cost centers.',
    commonFields: ['transfer-from', 'transfer-to', 'transfer-amount', 'approval-status'],
  },
  '/positions': {
    name: 'Position Budgeting',
    description: 'Manage position budgets, track vacancies, and plan for salary expenses.',
    commonFields: ['fte', 'headcount', 'position-budget', 'salary-savings', 'vacant-positions'],
  },
  '/positions/management': {
    name: 'Position Management',
    description: 'View and manage individual positions, incumbents, and compensation details.',
    commonFields: ['fte', 'headcount', 'benefit-rate', 'position-budget'],
  },
  '/reports': {
    name: 'Reports',
    description: 'Generate and view financial reports, budget summaries, and analytics.',
    commonFields: ['budget-variance', 'ytd-actuals', 'burn-rate', 'projected-year-end'],
  },
  '/transactions': {
    name: 'Transactions',
    description: 'View detailed transaction history and drill down into spending.',
    commonFields: ['posting-date', 'transaction-date', 'account-code', 'cost-center'],
  },
  '/approvals': {
    name: 'Approvals',
    description: 'Review and process pending approval requests.',
    commonFields: ['approval-status', 'approver', 'workflow-step'],
  },
  '/grants': {
    name: 'Grants & Sponsored Projects',
    description: 'Manage grant budgets, track spending, and monitor compliance.',
    commonFields: ['award-amount', 'direct-costs', 'indirect-costs', 'cost-share'],
  },
  '/chart-of-accounts': {
    name: 'Chart of Accounts',
    description: 'View and manage the account structure and coding.',
    commonFields: ['fund', 'cost-center', 'account-code', 'program', 'project'],
  },
};

/**
 * Get field definition by key
 */
export function getFieldDefinition(fieldKey) {
  const key = fieldKey?.toLowerCase().replace(/\s+/g, '-');
  return FIELD_GLOSSARY.find(f => f.key === key) || null;
}

/**
 * Search field glossary
 */
export function searchFieldGlossary(query, limit = 5) {
  if (!query) return [];

  const queryLower = query.toLowerCase();
  const queryTokens = queryLower.split(/\s+/).filter(Boolean);

  const scored = FIELD_GLOSSARY.map(field => {
    const searchable = [
      field.label,
      field.description,
      field.key,
      ...(field.context || []),
    ].join(' ').toLowerCase();

    const score = queryTokens.reduce((acc, token) =>
      acc + (searchable.includes(token) ? 1 : 0), 0);

    return { field, score };
  })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return scored.map(({ field }) => field);
}

/**
 * Get page context by path
 */
export function getPageContext(pathname) {
  // Exact match first
  if (PAGE_CONTEXT[pathname]) {
    return PAGE_CONTEXT[pathname];
  }

  // Try prefix match for nested routes
  const sortedPaths = Object.keys(PAGE_CONTEXT).sort((a, b) => b.length - a.length);
  for (const path of sortedPaths) {
    if (pathname.startsWith(path)) {
      return PAGE_CONTEXT[path];
    }
  }

  return null;
}
